import type { WorkoutKind } from '@gramas/shared'
import { Button, Field, NumberField, Pill, Sheet } from '@gramas/ui'
import { useEffect, useState } from 'react'
import type { LocalWorkout } from '../../db/dexie.js'
import { deleteWorkout, restoreWorkout, saveWorkout } from '../../db/repo.js'
import { sassToast } from '../../sass/toast.js'
import { syncNow } from '../../sync/engine.js'

export interface WorkoutSheetProps {
  open: boolean
  date: string
  /** Body weight on the day, so an unknown burn can be estimated from METs. */
  weightKg: number | null
  existing: LocalWorkout | null
  onOpenChange: (open: boolean) => void
}

export const KIND_LABEL: Record<WorkoutKind, string> = {
  walk: 'Ėjimas',
  run: 'Bėgimas',
  ride: 'Dviratis',
  swim: 'Plaukimas',
  gym: 'Salė',
  hiit: 'HIIT',
  sport: 'Sportas',
  other: 'Kita',
}

/**
 * Metabolic equivalents, from the Compendium of Physical Activities.
 *
 * These exist so the kcal field has a defensible starting number rather than a
 * blank the user has to invent. They assume a moderate pace, which is why the
 * estimate is offered as a prefill and stays editable — a band that measured
 * heart rate knows more than a table does.
 */
const MET: Record<WorkoutKind, number> = {
  walk: 3.5,
  run: 9.8,
  ride: 7.5,
  swim: 7.0,
  gym: 5.0,
  hiit: 8.0,
  sport: 7.0,
  other: 4.0,
}

/** kcal ≈ MET × kg × hours. Null when either input is unknown. */
export function estimateKcal(
  kind: WorkoutKind,
  durationMin: number | null,
  weightKg: number | null,
): number | null {
  if (durationMin == null || weightKg == null || durationMin <= 0) return null
  return Math.round((MET[kind] * weightKg * durationMin) / 60)
}

const KIND_ORDER: WorkoutKind[] = ['walk', 'run', 'ride', 'gym', 'hiit', 'swim', 'sport', 'other']

export function WorkoutSheet({ open, date, weightKg, existing, onOpenChange }: WorkoutSheetProps) {
  const [kind, setKind] = useState<WorkoutKind>('walk')
  const [name, setName] = useState('')
  const [durationMin, setDurationMin] = useState<number | null>(null)
  const [kcal, setKcal] = useState<number | null>(null)
  const [distanceKm, setDistanceKm] = useState<number | null>(null)
  const [touchedKcal, setTouchedKcal] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setKind(existing?.kind ?? 'walk')
    setName(existing?.name ?? '')
    setDurationMin(existing?.durationMin ?? null)
    setKcal(existing?.kcal ?? null)
    setDistanceKm(existing?.distanceKm ?? null)
    setTouchedKcal(existing?.kcal != null)
  }, [open, existing])

  const estimate = estimateKcal(kind, durationMin, weightKg)
  // Only fills a field the user has not taken over. Overwriting a figure read
  // off a watch with a table's guess would be worse than leaving it blank.
  const shownKcal = touchedKcal ? kcal : estimate

  const save = async () => {
    if (saving || (durationMin == null && shownKcal == null)) return
    setSaving(true)
    try {
      await saveWorkout({
        id: existing?.id,
        logDate: date,
        kind,
        name: name.trim() || null,
        durationMin,
        kcal: shownKcal,
        distanceKm,
        source: existing?.source ?? 'manual',
      })
      void syncNow()
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!existing) return
    const { id } = existing
    const name = existing.name ?? KIND_LABEL[existing.kind]
    await deleteWorkout(id)
    void syncNow()
    onOpenChange(false)
    sassToast('workout_deleted', { itemName: name }, `Pašalinta: ${name}`, {
      label: 'Atšaukti',
      run: async () => {
        await restoreWorkout(id)
        void syncNow()
      },
    })
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={existing ? 'Treniruotė' : 'Nauja treniruotė'}
      description={date}
      footer={
        <div className="flex gap-2">
          {existing && (
            <Button variant="danger" onClick={() => void remove()}>
              Šalinti
            </Button>
          )}
          <Button
            variant="primary"
            block
            size="lg"
            disabled={saving || (durationMin == null && shownKcal == null)}
            onClick={() => void save()}
          >
            {saving ? 'Saugoma…' : 'Išsaugoti'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        {/* A 4×2 grid rather than a segmented control: eight options in one row
            would put each label below a legible size on a phone. */}
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-sm text-dim">Veikla</legend>
          <div className="grid grid-cols-4 gap-1.5">
            {KIND_ORDER.map((k) => (
              <Pill
                key={k}
                active={kind === k}
                onClick={() => setKind(k)}
                className="h-10 w-full px-1"
              >
                {KIND_LABEL[k]}
              </Pill>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Trukmė" suffix="min" value={durationMin} onChange={setDurationMin} />
          <NumberField
            label="Sudeginta"
            suffix="kcal"
            hint={!touchedKcal && estimate != null ? 'Įvertis pagal svorį' : undefined}
            value={shownKcal}
            onChange={(value) => {
              setTouchedKcal(true)
              setKcal(value)
            }}
          />
        </div>

        <NumberField
          label="Atstumas"
          suffix="km"
          hint="Nebūtina"
          value={distanceKm}
          onChange={setDistanceKm}
        />

        <Field
          label="Pavadinimas"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={KIND_LABEL[kind]}
        />

        {weightKg == null && (
          <p className="text-xs text-mute">
            Įrašykite svorį, kad kalorijos būtų įvertintos automatiškai.
          </p>
        )}
      </div>
    </Sheet>
  )
}
